/**
 * Duplicates (owner, 2026-10-04; findings A1 and A9): the Catholic KK number is one per live
 * family of a parish and the NIK one per live person. The server refuses an op that would give
 * a second holder one of them (`duplicate_kk_number`, `duplicate_nik`); the device keeps that
 * op and sends it again on every sync, its value refreshed from the row as the clerk has fixed
 * it since (`segarkanOpGanda`), so nothing is lost and the fix travels with the original
 * creation. A device that is online asks the same question before a save (POST /sync/ganda),
 * and learns the parish's next KK number from every family of the parish, not only those its
 * own database holds (a ketua's device holds their lingkungan only).
 *
 * The NIK never travels in a URL: the request is a POST body, and the answer holds booleans.
 */
import { z } from 'zod'
import type { Peran } from '../schema/contract'
import { nikSchema, uuidSchema } from '../validators/kolom'
import type { Op, Row } from './ops'

/** The column each refusal is about, by table. */
export const KOLOM_GANDA = {
  keluarga: 'nomor_kk_katolik',
  umat: 'nik',
} as const

export type TabelGanda = keyof typeof KOLOM_GANDA

/** The refusal of each table's duplicate. */
export const ALASAN_GANDA = {
  keluarga: 'duplicate_kk_number',
  umat: 'duplicate_nik',
} as const satisfies Record<TabelGanda, string>

export type AlasanGanda = (typeof ALASAN_GANDA)[TabelGanda]

export function adalahTabelGanda(table: string): table is TabelGanda {
  return table === 'keluarga' || table === 'umat'
}

export function adalahAlasanGanda(reason: string | null | undefined): reason is AlasanGanda {
  return reason === ALASAN_GANDA.keluarga || reason === ALASAN_GANDA.umat
}

/** The value as compared: trimmed text, or null when there is none. */
export function nilaiGanda(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const t = value.trim()
  return t === '' ? null : t
}

/**
 * A KK number of digits without its leading zeros ("811" for "0811", a zero a spreadsheet ate);
 * null for a number that is not all digits. Impor reads a file's numbers against the parish's
 * families with it (import/baris `keluargaDenganKk`).
 */
export function angkaNomorKk(nomor: string): string | null {
  return /^\d+$/.test(nomor) ? nomor.replace(/^0+(?=\d)/, '') : null
}

/**
 * A Catholic KK number as the duplicate rules compare it, a live family's and a retired one alike
 * (release 14 and its follow-up): the trimmed text (`nilaiGanda`), and for a number of digits,
 * without its leading zeros (`angkaNomorKk`, Impor's rule), so "855" and "00855" are "0855" too.
 * Null when there is no number.
 */
export function kunciNomorKk(value: unknown): string | null {
  const nomor = nilaiGanda(value)
  return nomor === null ? null : (angkaNomorKk(nomor) ?? nomor)
}

/** The value as each duplicate rule compares it: a KK number by `kunciNomorKk`, a NIK trimmed. */
export function kunciGanda(table: TabelGanda, value: unknown): string | null {
  return table === 'keluarga' ? kunciNomorKk(value) : nilaiGanda(value)
}

/** Whether `nomor` is one of a parish's retired KK numbers, compared as `kunciNomorKk`. */
export function nomorKkPensiun(nomor: unknown, pensiun: Iterable<unknown>): boolean {
  const kunci = kunciNomorKk(nomor)
  if (kunci === null) return false
  for (const p of pensiun) if (kunciNomorKk(p) === kunci) return true
  return false
}

/**
 * The op a device sends again after a duplicate refusal: the refused column as the row holds it
 * now (the clerk changed the number or the NIK, or cleared it), and the row's deletion when the
 * clerk deleted it instead (final review I-1: a deleted row is no duplicate, so the op passes
 * and its card closes, where the old value alone was refused at every sync); every other field
 * as it was. An op that does not carry the column, or a row the device no longer holds, goes
 * unchanged.
 */
export function segarkanOpGanda(op: Op, row: Row | null): Op {
  if (!adalahTabelGanda(op.table) || row === null) return op
  const kolom = KOLOM_GANDA[op.table]
  if (!(kolom in op.fields)) return op
  const segar: Record<string, unknown> = {}
  const sekarang = row[kolom] ?? null
  if (sekarang !== (op.fields[kolom] ?? null)) segar[kolom] = sekarang
  const dihapus = row.dihapus_pada
  if (typeof dihapus === 'string' && dihapus !== '' && op.fields.dihapus_pada !== dihapus) {
    segar.dihapus_pada = dihapus
  }
  return Object.keys(segar).length === 0 ? op : { ...op, fields: { ...op.fields, ...segar } }
}

/**
 * The longest parish number SATU counts (release 18 review C-1). A longer run of digits is not
 * SATU's: a government number typed into the old "Nomor KK Katolik" field, or read into it by an
 * Impor before release 18. It can never meet a short next number, and above 2^53 a double holds
 * even integers only, so "…032" + 1 came back as "…032" itself and generation stood still.
 */
export const NOMOR_PAROKI_PANJANG_MAKS = 10

/** Whether `nomor` is a parish number as generation counts it: one to ten digits. */
export function adalahNomorParoki(nomor: string): boolean {
  return /^\d{1,10}$/.test(nomor)
}

/** A number of digits without its leading zeros ("0" stays "0"). */
const tanpaNol = (nomor: string): string => nomor.replace(/^0+(?=\d)/, '')

/**
 * Two numbers of digits compared by value: -1, 0 or 1. By length, then as text, once the leading
 * zeros go; never through a double (release 18 review C-1).
 */
export function bandingkanNomorKk(a: string, b: string): -1 | 0 | 1 {
  const x = tanpaNol(a)
  const y = tanpaNol(b)
  if (x.length !== y.length) return x.length < y.length ? -1 : 1
  return x === y ? 0 : x < y ? -1 : 1
}

/** The number after `nomor` (digits only), exact at any length, as wide as it or four digits. */
export function nomorKkSesudah(nomor: string): string {
  return String(BigInt(nomor) + 1n).padStart(Math.max(4, nomor.length), '0')
}

/**
 * max(parish numbers) + 1, padded to the widest of them (at least four digits). Only parish-length
 * numbers count (`adalahNomorParoki`); text and longer runs of digits are ignored.
 */
export function nomorKkBerikutnya(nomor: Iterable<string | null | undefined>): string {
  let max = '0'
  let lebar = 4
  for (const mentah of nomor) {
    const n = String(mentah ?? '').trim()
    if (!adalahNomorParoki(n)) continue
    if (bandingkanNomorKk(n, max) > 0) max = n
    lebar = Math.max(lebar, n.length)
  }
  return nomorKkSesudah(max).padStart(lebar, '0')
}

/**
 * At most this many NIKs per question: a KK grid's rows, or a chunk of an Impor file (release 14:
 * 500, so a large file stays within the server's 120 questions per 10 minutes).
 */
export const GANDA_NIK_MAKS = 500

/** At most this many KK numbers per question (Impor asks for a chunk of a file's families). */
export const GANDA_KK_MAKS = 200

/** The longest KK number a question carries. */
export const GANDA_KK_PANJANG = 32

/** The longest Kode keluarga a question carries. */
export const GANDA_KODE_PANJANG = 64

/** A government KK number as a question carries it: 16 digits, nothing else. */
const nomorKkPemerintahSchema = z.string().regex(/^\d{16}$/)

/** A Kode keluarga as a question carries it: trimmed, not empty. */
const kodeKeluargaSchema = z.string().trim().min(1).max(GANDA_KODE_PANJANG)

export const gandaRequestSchema = z.object({
  /** null: no KK number to check; the answer still proposes the next one. */
  nomor_kk_katolik: z.string().trim().max(GANDA_KK_PANJANG).nullable(),
  /** The family being edited, never counted as another holder. */
  keluarga_id: uuidSchema.nullable(),
  nik: z.array(z.object({ nik: nikSchema, umat_id: uuidSchema.nullable() })).max(GANDA_NIK_MAKS),
  /**
   * Impor (cross-cutting round, 2026-10-04): the KK numbers of new families in a file, each
   * asked as `nomor_kk_katolik` is, against every live family of the parish. Absent from the
   * other screens' questions (release 18 sends it from none; an app on schema 13 from Impor).
   * Refused (403) to a role that cannot Impor (`PERAN_IMPOR`, re-review N-2).
   */
  nomor_kk_daftar: z
    .array(z.string().trim().min(1).max(GANDA_KK_PANJANG))
    .max(GANDA_KK_MAKS)
    .optional(),
  /** An admin keuskupan names the parish; every parish role asks about its own. */
  paroki_id: uuidSchema.nullish(),
  /**
   * Release 18 (owner, 2026-10-08): a government KK number another live family may also hold, in
   * the asker's parish or another (a warning, never a refusal). Counted with the NIKs toward the
   * hour's bound: it is identifying, as a NIK is. Absent from an older app's question.
   */
  nomor_kk_pemerintah: nomorKkPemerintahSchema.nullish(),
  /** Release 18: a Kode keluarga another live family of the asker's parish may also hold. */
  kode_keluarga: kodeKeluargaSchema.nullish(),
  /**
   * Impor's new families: their government numbers, each asked as `nomor_kk_pemerintah` is.
   * Refused (403) to a role that cannot Impor (`PERAN_IMPOR`, review I-1), as is the next.
   */
  nomor_kk_pemerintah_daftar: z.array(nomorKkPemerintahSchema).max(GANDA_KK_MAKS).optional(),
  /** Impor's new families: their codes, each asked as `kode_keluarga` is. */
  kode_keluarga_daftar: z.array(kodeKeluargaSchema).max(GANDA_KK_MAKS).optional(),
})
export type GandaRequest = z.infer<typeof gandaRequestSchema>

/**
 * Review I-1: a ketua (lingkungan or stasi) is scoped to their lingkungan (spec 9.4). The answer
 * to a ketua names only the heads of families inside it; a holder elsewhere in the parish is
 * this flag alone, never a name. Absent from the answer to every other role, and from a server
 * before this fix.
 */
const luarLingkupSchema = z.boolean().optional()

/**
 * Who else holds a government KK number (release 18): the heads of the asker's parish's live
 * families holding it (null for a family without a recorded head; a ketua's: within their
 * lingkungan, `luar_lingkup` for the rest of the parish), and whether a family of another parish
 * holds it too, never which.
 */
export const pemakaiKkSchema = z.object({
  paroki_ini: z.array(z.string().nullable()),
  luar_lingkup: luarLingkupSchema,
  paroki_lain: z.boolean(),
})
export type PemakaiKk = z.infer<typeof pemakaiKkSchema>

/** Who else holds a Kode keluarga: the heads of the asker's parish's live families only. */
export const pemakaiKodeSchema = z.object({
  paroki_ini: z.array(z.string().nullable()),
  luar_lingkup: luarLingkupSchema,
})
export type PemakaiKode = z.infer<typeof pemakaiKodeSchema>

/** The most heads one answer names for one value. */
export const GANDA_KEPALA_MAKS = 5

/**
 * The roles whose questions may carry lists (`nomor_kk_daftar`, `nomor_kk_pemerintah_daftar`,
 * `kode_keluarga_daftar`): the roles that Impor (review I-1, re-review N-2), the parish's staff
 * and an admin keuskupan working inside a parish (plan 10: that parish's Admin Paroki shelf,
 * Impor included; the server reads its account's role). Every other role asks one value at a time.
 */
export const PERAN_IMPOR: readonly Peran[] = ['admin_paroki', 'staf_paroki', 'admin_keuskupan']

export function bolehImpor(peran: Peran): boolean {
  return PERAN_IMPOR.includes(peran)
}

/**
 * The places a question takes of the hour's identifying-number bound: its NIKs, its government
 * KK numbers and (review I-1) its Kode keluarga, which are guessable and answered with heads.
 */
export function jumlahNomorDitanya(req: GandaRequest): number {
  const satu = (v: string | null | undefined) => (v === null || v === undefined ? 0 : 1)
  return (
    req.nik.length +
    satu(req.nomor_kk_pemerintah) +
    (req.nomor_kk_pemerintah_daftar?.length ?? 0) +
    satu(req.kode_keluarga) +
    (req.kode_keluarga_daftar?.length ?? 0)
  )
}

export const gandaResponseSchema = z.object({
  /**
   * null: no number asked, or no parish to ask about. True also for a number the parish retired
   * (owner, 2026-10-04): an app before release 14 then says it is taken.
   */
  nomor_kk_dipakai: z.boolean().nullable(),
  /**
   * With `nomor_kk_dipakai` true: whether it is taken only because the parish retired it (a
   * family that moved parish took it away), so the screen words that reason. Never which family
   * or parish held it. Absent from a server before release 14.
   */
  nomor_kk_pensiun: z.boolean().optional(),
  /** The parish's next KK number from every live family of it; null without a parish. */
  nomor_kk_usulan: z.string().nullable(),
  /** One per asked NIK, in order. */
  nik_dipakai: z.array(z.boolean()),
  /**
   * One per number of `nomor_kk_daftar`, in order, when it was asked and there is a parish to
   * ask about; absent otherwise (and from a server before this release: the device's own check
   * then stands).
   */
  nomor_kk_daftar_dipakai: z.array(z.boolean()).optional(),
  /** One per number of `nomor_kk_daftar`, as `nomor_kk_pensiun` is for the one number. */
  nomor_kk_daftar_pensiun: z.array(z.boolean()).optional(),
  /** Release 18: for `nomor_kk_pemerintah`, when asked; absent otherwise and from an older server. */
  nomor_kk_pemerintah_dipakai: pemakaiKkSchema.optional(),
  /** Release 18: for `kode_keluarga`, when asked and there is a parish to ask about. */
  kode_keluarga_dipakai: pemakaiKodeSchema.optional(),
  /** One per number of `nomor_kk_pemerintah_daftar`, in order, when asked. */
  nomor_kk_pemerintah_daftar_dipakai: z.array(pemakaiKkSchema).optional(),
  /** One per code of `kode_keluarga_daftar`, in order, when asked with a parish. */
  kode_keluarga_daftar_dipakai: z.array(pemakaiKodeSchema).optional(),
})
export type GandaResponse = z.infer<typeof gandaResponseSchema>
